import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { 
  FiArrowLeft, FiMapPin, FiClock, FiCheckCircle, FiXCircle, 
  FiNavigation, FiFlag, FiTarget, FiRefreshCcw, FiChevronDown, 
  FiChevronUp, FiTruck, FiUser, FiPhone, FiInfo, FiCalendar,
  FiArrowRight, FiCornerDownRight, FiMap
} from 'react-icons/fi';
import { ClipLoader } from 'react-spinners';
import axios from 'axios';
import BaseUrl from '../../../Url/BaseUrl';

const ViewRouteDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { routePathNo } = useParams();
  
  // Get data from navigation state
  const { 
    route, 
    villages: allVillages, 
    busRoutesMap, 
    arrivalData: initialArrivalData, 
    returnData: initialReturnData,
    fullApiResponse,
    searchParams
  } = location.state || {};

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedStops, setExpandedStops] = useState({});
  const [activeTab, setActiveTab] = useState('stoppages');
  const [isMobile, setIsMobile] = useState(false);
  
  // Data states
  const [routeDetails, setRouteDetails] = useState(null);
  
  // ------------------------------------------------------------
  // TWO SEPARATE LISTS — same pattern as BusDetails.jsx
  // forwardStops come from arrival[]
  // returnStops come from return[]
  // They may have DIFFERENT bus stands, so we keep them separate
  // ------------------------------------------------------------
  const [forwardStops, setForwardStops] = useState([]);
  const [returnStops, setReturnStops] = useState([]);

  const [arrivalData, setArrivalData] = useState(initialArrivalData || []);
  const [returnData, setReturnData] = useState(initialReturnData || []);
  const [villages, setVillages] = useState(allVillages || []);

  const API_BASE_URL = BaseUrl.baseurl;

  useEffect(() => {
    const checkScreenSize = () => setIsMobile(window.innerWidth <= 768);
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    if (!route) {
      fetchRouteDetails();
    } else {
      setRouteDetails(route);
      processStops();
      setLoading(false);
    }
  }, [route]);

  const fetchRouteDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}RoutePaths/${routePathNo}`);
      if (response.data) {
        setRouteDetails({
          routePathNo: response.data.routePathNo,
          routePathName: response.data.routePathName,
          busRouteName: response.data.busRoute?.routeName || 'N/A',
          status: response.data.status,
          routePath: response.data
        });
      }
    } catch (err) {
      console.error('Error fetching route details:', err);
      setError('Failed to load route details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // Format time from "HH:MM:SS" to "H:MM AM/PM"
  // ============================================================
  const formatTime = (timeStr) => {
    if (!timeStr) return null;
    try {
      const parts = timeStr.split(':');
      if (parts.length >= 2) {
        const hours = parseInt(parts[0]);
        const minutes = parts[1];
        const period = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        return `${displayHours}:${minutes} ${period}`;
      }
      return timeStr;
    } catch {
      return timeStr;
    }
  };

  // ============================================================
  // Helper: resolve stop name from busStand object
  // ============================================================
  const resolveStopName = (busStand, fallbackCode = '') => {
    if (!busStand) return fallbackCode || 'Unknown Stop';
    const stopName = busStand.busStandName || fallbackCode || 'Unknown Stop';
    let villageName = '';
    if (busStand.cityVillage) {
      villageName = busStand.cityVillage.cityVillageName || '';
    }
    return villageName ? `${stopName}, ${villageName}` : stopName;
  };

  // ============================================================
  // Process stops — build TWO SEPARATE LISTS
  // Forward = from arrival[]  (use arrivalTime)
  // Return  = from return[]   (use departureTime)
  // ============================================================
  const processStops = () => {
    if (!route) return;

    const routeVillageCodes = route.villages?.map(v => v.villageCode) || [];

    // Filter arrival data for this route's villages
    const routeArrivalTimings = arrivalData.filter(
      item => item.busStand?.cityVillage?.cityVillageCode && 
      routeVillageCodes.includes(item.busStand.cityVillage.cityVillageCode)
    );

    // Filter return data for this route's villages
    const routeReturnTimings = returnData.filter(
      item => item.busStand?.cityVillage?.cityVillageCode && 
      routeVillageCodes.includes(item.busStand.cityVillage.cityVillageCode)
    );

    // ---------------- FORWARD ROUTE ----------------
    // Sort by arrivalTime ascending
    const sortedForward = [...routeArrivalTimings].sort((a, b) => {
      const timeA = a.arrivalTime || a.departureTime || '';
      const timeB = b.arrivalTime || b.departureTime || '';
      return timeA.localeCompare(timeB);
    });

    const forwardList = sortedForward
      .filter(timing => timing.arrivalTime) // only show stops with arrivalTime
      .map((timing, idx) => {
        const busStand = timing.busStand;
        const gps = busStand?.latitude && busStand?.longitude
          ? { lat: busStand.latitude, lng: busStand.longitude }
          : null;
        return {
          id: `fwd-${timing.id || idx}`,
          rawId: timing.id,
          stopName: resolveStopName(busStand, timing.busStandCode),
          busStandCode: timing.busStandCode,
          cityVillageCode: busStand?.cityVillage?.cityVillageCode,
          villageName: busStand?.cityVillage?.cityVillageName || '',
          time: formatTime(timing.arrivalTime),
          rawTime: timing.arrivalTime,
          gps,
          latitude: busStand?.latitude,
          longitude: busStand?.longitude,
          elevation: busStand?.elevation,
          status: timing.status
        };
      });

    // ---------------- RETURN ROUTE ----------------
    // Sort by departureTime ascending
    const sortedReturn = [...routeReturnTimings].sort((a, b) => {
      const timeA = a.departureTime || a.arrivalTime || '';
      const timeB = b.departureTime || b.arrivalTime || '';
      return timeA.localeCompare(timeB);
    });

    const returnList = sortedReturn
      .filter(timing => timing.departureTime) // only show stops with departureTime
      .map((timing, idx) => {
        const busStand = timing.busStand;
        const gps = busStand?.latitude && busStand?.longitude
          ? { lat: busStand.latitude, lng: busStand.longitude }
          : null;
        return {
          id: `ret-${timing.id || idx}`,
          rawId: timing.id,
          stopName: resolveStopName(busStand, timing.busStandCode),
          busStandCode: timing.busStandCode,
          cityVillageCode: busStand?.cityVillage?.cityVillageCode,
          villageName: busStand?.cityVillage?.cityVillageName || '',
          time: formatTime(timing.departureTime),
          rawTime: timing.departureTime,
          gps,
          latitude: busStand?.latitude,
          longitude: busStand?.longitude,
          elevation: busStand?.elevation,
          status: timing.status
        };
      });

    // Fallback: if no arrival data, build forward from route.villages
    if (forwardList.length === 0 && route.villages) {
      const fallbackForward = route.villages.map((v, index) => ({
        id: `fwd-fallback-${index}`,
        rawId: index,
        stopName: v.villageName || v.villageCode,
        busStandCode: '',
        cityVillageCode: v.villageCode,
        villageName: v.villageName,
        time: null,
        rawTime: null,
        gps: null,
        status: true
      }));
      setForwardStops(fallbackForward);
    } else {
      setForwardStops(forwardList);
    }

    setReturnStops(returnList);
  };

  useEffect(() => {
    if (route) {
      processStops();
    }
  }, [route, arrivalData, returnData]);

  const toggleStopExpansion = (stopId) => {
    setExpandedStops(prev => ({ ...prev, [stopId]: !prev[stopId] }));
  };

  const openGPSLocation = (coordinates) => {
    if (coordinates) {
      const url = `https://www.google.com/maps?q=${coordinates.lat},${coordinates.lng}`;
      window.open(url, '_blank');
    }
  };

  const getVillageName = (code) => {
    const village = villages.find(v => v.cityVillageCode === code);
    return village ? village.cityVillageName : code;
  };

  // Loading state
  if (loading) {
    return (
      <div className="view-route-container">
        <div className="loading-state">
          <ClipLoader size={50} color="#00BCD4" />
          <p>Loading route details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !routeDetails) {
    return (
      <div className="view-route-container">
        <div className="error-state">
          <FiInfo size={48} />
          <h3>Error Loading Route</h3>
          <p>{error || 'Route details not found'}</p>
          <button className="btn-secondary" onClick={() => navigate(-1)}>
            <FiArrowLeft /> Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="view-route-container">
      {/* Header */}
      <div className="view-header">
        <button className="btn-back" onClick={() => navigate(-1)}>
          <FiArrowLeft /> Back to Routes
        </button>
        
        <div className="header-info">
          <div className="route-title-section">
            <h1>
              <FiNavigation className="title-icon" />
              {routeDetails.routePathName || 'Route Details'}
            </h1>
            <div className="route-meta">
              <span className="route-path-no">
                <FiMapPin /> Path No: {routeDetails.routePathNo}
              </span>
              {routeDetails.busRouteName && routeDetails.busRouteName !== 'N/A' && (
                <span className="bus-route-name">
                  <FiTruck /> {routeDetails.busRouteName}
                </span>
              )}
              <span className={`status-badge ${routeDetails.status ? 'active' : 'inactive'}`}>
                {routeDetails.status ? (
                  <><FiCheckCircle /> Active</>
                ) : (
                  <><FiXCircle /> Inactive</>
                )}
              </span>
            </div>
          </div>
        </div>

        {searchParams && (
          <div className="search-context">
            <span className="context-label">Searched Route:</span>
            <span className="context-value">
              {getVillageName(searchParams.fromCityVillageCode)} → {getVillageName(searchParams.toCityVillageCode)}
            </span>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue">
            <FiMapPin />
          </div>
          <div className="stat-content">
            <span className="stat-label">Forward Stops</span>
            <span className="stat-value">{forwardStops.length}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple">
            <FiCornerDownRight />
          </div>
          <div className="stat-content">
            <span className="stat-label">Return Stops</span>
            <span className="stat-value">{returnStops.length}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green">
            <FiMap />
          </div>
          <div className="stat-content">
            <span className="stat-label">Villages Covered</span>
            <span className="stat-value">{route.villages?.length || 0}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange">
            <FiClock />
          </div>
          <div className="stat-content">
            <span className="stat-label">Total Timings</span>
            <span className="stat-value">
              {forwardStops.filter(s => s.time).length + returnStops.filter(s => s.time).length}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs-header">
          <button
            className={`tab-btn ${activeTab === 'stoppages' ? 'active' : ''}`}
            onClick={() => setActiveTab('stoppages')}
          >
            <FiMapPin /> Stoppages ({forwardStops.length + returnStops.length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'villages' ? 'active' : ''}`}
            onClick={() => setActiveTab('villages')}
          >
            <FiFlag /> Villages ({route.villages?.length || 0})
          </button>
        </div>

        <div className="tab-content">
          {/* ====================== STOPPAGES TAB ====================== */}
          {activeTab === 'stoppages' && (
            <div className="stoppages-container">
              {forwardStops.length === 0 && returnStops.length === 0 ? (
                <div className="empty-state">
                  <FiMapPin size={48} />
                  <h3>No Stoppages Found</h3>
                  <p>No stop information available for this route.</p>
                </div>
              ) : (
                <div className="route-columns-container">
                  {/* ============ FORWARD ROUTE COLUMN ============ */}
                  <div className="route-column">
                    <div className="route-column-header forward-column-header">
                      <FiArrowRight />
                      Forward Route (Arrival) — {forwardStops.length} stops
                    </div>
                    {forwardStops.length === 0 ? (
                      <div className="empty-column">No forward stops</div>
                    ) : (
                      forwardStops.map((stop, index) => (
                        <div key={stop.id} className="stop-card forward-stop-card">
                          <div className="stop-row">
                            <div className="stop-name-col">
                              <div className="stop-number">{index + 1}</div>
                              <div className="stop-details">
                                <div className="stop-name">{stop.stopName}</div>
                                {stop.busStandCode && (
                                  <div className="stop-code">({stop.busStandCode})</div>
                                )}
                              </div>
                            </div>
                            <div className={`time-badge ${stop.time ? 'forward-time-badge' : 'empty-time-badge'}`}>
                              {stop.time ? (
                                <>
                                  <span className="time-value forward-time-value">{stop.time}</span>
                                  <span className="time-sub-label">Arrival</span>
                                </>
                              ) : (
                                <span className="na-value">N/A</span>
                              )}
                            </div>
                          </div>
                          {(stop.gps || stop.busStandCode) && (
                            <div className="stop-actions-row">
                              {stop.gps && (
                                <button
                                  className="btn-location"
                                  onClick={() => openGPSLocation(stop.gps)}
                                >
                                  <FiMapPin /> Location
                                </button>
                              )}
                              <button
                                className="btn-expand"
                                onClick={() => toggleStopExpansion(stop.id)}
                              >
                                {expandedStops[stop.id] ? <FiChevronUp /> : <FiChevronDown />}
                              </button>
                            </div>
                          )}
                          {expandedStops[stop.id] && (
                            <div className="stop-expanded">
                              <div className="expanded-grid">
                                <div className="expanded-item">
                                  <span className="expanded-label">Stop Code:</span>
                                  <span className="expanded-value">{stop.busStandCode || 'N/A'}</span>
                                </div>
                                <div className="expanded-item">
                                  <span className="expanded-label">Village Code:</span>
                                  <span className="expanded-value">{stop.cityVillageCode || 'N/A'}</span>
                                </div>
                                <div className="expanded-item">
                                  <span className="expanded-label">Village Name:</span>
                                  <span className="expanded-value">{stop.villageName || 'N/A'}</span>
                                </div>
                                {stop.latitude && stop.longitude && (
                                  <>
                                    <div className="expanded-item">
                                      <span className="expanded-label">Latitude:</span>
                                      <span className="expanded-value">{stop.latitude}</span>
                                    </div>
                                    <div className="expanded-item">
                                      <span className="expanded-label">Longitude:</span>
                                      <span className="expanded-value">{stop.longitude}</span>
                                    </div>
                                  </>
                                )}
                                {stop.elevation && (
                                  <div className="expanded-item">
                                    <span className="expanded-label">Elevation:</span>
                                    <span className="expanded-value">{stop.elevation}m</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>

                  {/* ============ RETURN ROUTE COLUMN ============ */}
                  <div className="route-column">
                    <div className="route-column-header return-column-header">
                      <FiCornerDownRight />
                      Return Route (Departure) — {returnStops.length} stops
                    </div>
                    {returnStops.length === 0 ? (
                      <div className="empty-column">No return stops</div>
                    ) : (
                      returnStops.map((stop, index) => (
                        <div key={stop.id} className="stop-card return-stop-card">
                          <div className="stop-row">
                            <div className="stop-name-col">
                              <div className="stop-number return-number">{index + 1}</div>
                              <div className="stop-details">
                                <div className="stop-name">{stop.stopName}</div>
                                {stop.busStandCode && (
                                  <div className="stop-code">({stop.busStandCode})</div>
                                )}
                              </div>
                            </div>
                            <div className={`time-badge ${stop.time ? 'return-time-badge' : 'empty-time-badge'}`}>
                              {stop.time ? (
                                <>
                                  <span className="time-value return-time-value">{stop.time}</span>
                                  <span className="time-sub-label">Departure</span>
                                </>
                              ) : (
                                <span className="na-value">N/A</span>
                              )}
                            </div>
                          </div>
                          {(stop.gps || stop.busStandCode) && (
                            <div className="stop-actions-row">
                              {stop.gps && (
                                <button
                                  className="btn-location"
                                  onClick={() => openGPSLocation(stop.gps)}
                                >
                                  <FiMapPin /> Location
                                </button>
                              )}
                              <button
                                className="btn-expand"
                                onClick={() => toggleStopExpansion(stop.id)}
                              >
                                {expandedStops[stop.id] ? <FiChevronUp /> : <FiChevronDown />}
                              </button>
                            </div>
                          )}
                          {expandedStops[stop.id] && (
                            <div className="stop-expanded">
                              <div className="expanded-grid">
                                <div className="expanded-item">
                                  <span className="expanded-label">Stop Code:</span>
                                  <span className="expanded-value">{stop.busStandCode || 'N/A'}</span>
                                </div>
                                <div className="expanded-item">
                                  <span className="expanded-label">Village Code:</span>
                                  <span className="expanded-value">{stop.cityVillageCode || 'N/A'}</span>
                                </div>
                                <div className="expanded-item">
                                  <span className="expanded-label">Village Name:</span>
                                  <span className="expanded-value">{stop.villageName || 'N/A'}</span>
                                </div>
                                {stop.latitude && stop.longitude && (
                                  <>
                                    <div className="expanded-item">
                                      <span className="expanded-label">Latitude:</span>
                                      <span className="expanded-value">{stop.latitude}</span>
                                    </div>
                                    <div className="expanded-item">
                                      <span className="expanded-label">Longitude:</span>
                                      <span className="expanded-value">{stop.longitude}</span>
                                    </div>
                                  </>
                                )}
                                {stop.elevation && (
                                  <div className="expanded-item">
                                    <span className="expanded-label">Elevation:</span>
                                    <span className="expanded-value">{stop.elevation}m</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ====================== VILLAGES TAB ====================== */}
          {activeTab === 'villages' && (
            <div className="villages-container">
              {!route.villages || route.villages.length === 0 ? (
                <div className="empty-state">
                  <FiFlag size={48} />
                  <h3>No Villages Found</h3>
                  <p>No village information available for this route.</p>
                </div>
              ) : (
                <div className="villages-timeline">
                  {route.villages.map((village, index) => {
                    const matchingForward = forwardStops.find(
                      s => s.cityVillageCode === village.villageCode
                    );
                    const matchingReturn = returnStops.find(
                      s => s.cityVillageCode === village.villageCode
                    );
                    
                    return (
                      <div key={village.villageCode} className="village-item">
                        <div className="village-connector">
                          <div className="village-dot"></div>
                          {index < route.villages.length - 1 && (
                            <div className="village-line"></div>
                          )}
                        </div>
                        <div className="village-content">
                          <div className="village-header">
                            <span className="village-index">{index + 1}</span>
                            <span className="village-name">
                              {village.villageName || getVillageName(village.villageCode)}
                            </span>
                            <span className="village-code">({village.villageCode})</span>
                          </div>
                          <div className="village-timings">
                            {matchingForward?.time && (
                              <span className="timing forward">
                                <FiArrowRight /> {matchingForward.time}
                              </span>
                            )}
                            {matchingReturn?.time && (
                              <span className="timing return">
                                <FiCornerDownRight /> {matchingReturn.time}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Styles */}
      <style jsx>{`
        .view-route-container {
          min-height: 100vh;
          background: #f8fafc;
          padding: 2rem;
        }

        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
          color: #64748b;
        }

        .loading-state p {
          margin-top: 1rem;
          font-size: 1.1rem;
        }

        .error-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
          text-align: center;
          color: #dc2626;
        }

        .error-state h3 {
          margin: 1rem 0 0.5rem;
          color: #1e293b;
        }

        .error-state p {
          margin-bottom: 1.5rem;
          color: #64748b;
        }

        .view-header {
          max-width: 1400px;
          margin: 0 auto 2rem;
          background: white;
          border-radius: 16px;
          padding: 1.5rem 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
        }

        .btn-back {
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          color: #475569;
          font-weight: 500;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
          margin-bottom: 1.5rem;
        }

        .btn-back:hover {
          background: #e2e8f0;
          color: #1e293b;
        }

        .route-title-section h1 {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 1.75rem;
          color: #1e293b;
          margin: 0 0 1rem 0;
        }

        .title-icon {
          color: #00BCD4;
        }

        .route-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          align-items: center;
        }

        .route-path-no,
        .bus-route-name {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.4rem 0.8rem;
          border-radius: 20px;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .route-path-no {
          background: #e3f2fd;
          color: #1565c0;
        }

        .bus-route-name {
          background: #f3e5f5;
          color: #7b1fa2;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.4rem 0.8rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .status-badge.active {
          background: #d4edda;
          color: #155724;
        }

        .status-badge.inactive {
          background: #f8d7da;
          color: #721c24;
        }

        .search-context {
          margin-top: 1rem;
          padding: 0.75rem 1rem;
          background: #fffbeb;
          border-radius: 8px;
          border-left: 4px solid #f59e0b;
          font-size: 0.95rem;
        }

        .context-label {
          color: #92400e;
          font-weight: 600;
          margin-right: 0.5rem;
        }

        .context-value {
          color: #78350f;
        }

        /* Stats Grid */
        .stats-grid {
          max-width: 1400px;
          margin: 0 auto 2rem;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .stat-card {
          background: white;
          border-radius: 12px;
          padding: 1.25rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
        }

        .stat-icon.blue {
          background: #e3f2fd;
          color: #1565c0;
        }

        .stat-icon.green {
          background: #e8f5e9;
          color: #2e7d32;
        }

        .stat-icon.purple {
          background: #f3e5f5;
          color: #7b1fa2;
        }

        .stat-icon.orange {
          background: #fff3e0;
          color: #e65100;
        }

        .stat-content {
          display: flex;
          flex-direction: column;
        }

        .stat-label {
          font-size: 0.85rem;
          color: #64748b;
          font-weight: 500;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
        }

        /* Tabs */
        .tabs-container {
          max-width: 1400px;
          margin: 0 auto;
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          overflow: hidden;
        }

        .tabs-header {
          display: flex;
          border-bottom: 2px solid #e2e8f0;
          background: #f8fafc;
        }

        .tab-btn {
          flex: 1;
          padding: 1rem 1.5rem;
          border: none;
          background: transparent;
          font-size: 1rem;
          font-weight: 600;
          color: #64748b;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          transition: all 0.2s;
          border-bottom: 3px solid transparent;
        }

        .tab-btn:hover {
          background: #f1f5f9;
          color: #334155;
        }

        .tab-btn.active {
          color: #00BCD4;
          border-bottom-color: #00BCD4;
          background: white;
        }

        .tab-content {
          padding: 1.5rem;
        }

        /* Stoppages */
        .stoppages-container {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        /* Two-column layout */
        .route-columns-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
        }

        .route-column {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .route-column-header {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.9rem 1rem;
          border-radius: 10px;
          font-weight: 700;
          font-size: 0.95rem;
          color: white;
          margin-bottom: 0.25rem;
        }

        .forward-column-header {
          background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%);
        }

        .return-column-header {
          background: linear-gradient(135deg, #2196f3 0%, #1565c0 100%);
        }

        .stop-card {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.2s;
        }

        .forward-stop-card {
          border-left: 4px solid #4caf50;
        }

        .return-stop-card {
          border-left: 4px solid #2196f3;
        }

        .stop-card:hover {
          border-color: #00BCD4;
          box-shadow: 0 4px 12px rgba(0, 188, 212, 0.1);
        }

        .stop-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 0.75rem;
          padding: 0.9rem 1rem;
        }

        .stop-name-col {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          flex: 1;
          min-width: 0;
        }

        .stop-number {
          width: 28px;
          height: 28px;
          background: #e8f5e9;
          color: #2e7d32;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.8rem;
          flex-shrink: 0;
        }

        .stop-number.return-number {
          background: #e3f2fd;
          color: #1565c0;
        }

        .stop-details {
          display: flex;
          flex-direction: column;
          gap: 0.15rem;
          min-width: 0;
        }

        .stop-name {
          font-weight: 600;
          color: #1e293b;
          font-size: 0.95rem;
          word-break: break-word;
        }

        .stop-code {
          font-size: 0.75rem;
          color: #64748b;
        }

        .time-badge {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 0.45rem 0.75rem;
          border-radius: 8px;
          min-width: 80px;
          text-align: center;
          gap: 0.15rem;
          flex-shrink: 0;
        }

        .forward-time-badge {
          background: #e8f5e9;
          border: 1.5px solid #4caf50;
        }

        .return-time-badge {
          background: #e3f2fd;
          border: 1.5px solid #2196f3;
        }

        .empty-time-badge {
          background: #f5f5f5;
          border: 1.5px dashed #cbd5e1;
        }

        .time-value {
          font-size: 0.9rem;
          font-weight: 800;
        }

        .forward-time-value {
          color: #2e7d32;
        }

        .return-time-value {
          color: #1565c0;
        }

        .time-sub-label {
          font-size: 0.7rem;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
        }

        .na-value {
          font-size: 0.85rem;
          color: #94a3b8;
          font-style: italic;
        }

        .stop-actions-row {
          display: flex;
          justify-content: flex-end;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border-top: 1px dashed #e2e8f0;
        }

        .btn-location {
          background: #4caf50;
          color: white;
          border: none;
          padding: 0.3rem 0.65rem;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 500;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          transition: all 0.2s;
        }

        .btn-location:hover {
          background: #388e3c;
        }

        .btn-expand {
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          color: #64748b;
          width: 28px;
          height: 28px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-expand:hover {
          background: #e2e8f0;
          color: #334155;
        }

        /* Expanded Details */
        .stop-expanded {
          padding: 1rem;
          background: #f8fafc;
          border-top: 1px dashed #e2e8f0;
        }

        .expanded-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 0.75rem;
        }

        .expanded-item {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
        }

        .expanded-label {
          font-size: 0.7rem;
          color: #64748b;
          font-weight: 500;
          text-transform: uppercase;
        }

        .expanded-value {
          font-size: 0.85rem;
          color: #1e293b;
          font-weight: 500;
        }

        /* Empty Column */
        .empty-column {
          padding: 2rem 1rem;
          text-align: center;
          color: #94a3b8;
          font-style: italic;
          background: #f8fafc;
          border-radius: 10px;
          border: 1.5px dashed #cbd5e1;
          font-size: 0.9rem;
        }

        /* Villages Timeline */
        .villages-container {
          padding: 1rem 0;
        }

        .villages-timeline {
          display: flex;
          flex-direction: column;
        }

        .village-item {
          display: flex;
          gap: 1rem;
        }

        .village-connector {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 24px;
        }

        .village-dot {
          width: 16px;
          height: 16px;
          background: #00BCD4;
          border-radius: 50%;
          border: 3px solid #e0f7fa;
          flex-shrink: 0;
        }

        .village-line {
          width: 2px;
          flex: 1;
          background: #e2e8f0;
          min-height: 40px;
        }

        .village-content {
          flex: 1;
          padding-bottom: 1.5rem;
        }

        .village-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .village-index {
          background: #e3f2fd;
          color: #1565c0;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          font-weight: 700;
        }

        .village-name {
          font-weight: 600;
          color: #1e293b;
          font-size: 1rem;
        }

        .village-code {
          font-size: 0.85rem;
          color: #64748b;
        }

        .village-timings {
          display: flex;
          gap: 1rem;
          margin-top: 0.5rem;
          flex-wrap: wrap;
        }

        .timing {
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          padding: 0.3rem 0.6rem;
          border-radius: 6px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .timing.forward {
          background: #e8f5e9;
          color: #2e7d32;
        }

        .timing.return {
          background: #e3f2fd;
          color: #1565c0;
        }

        /* Empty State */
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          text-align: center;
          color: #64748b;
        }

        .empty-state h3 {
          margin: 1rem 0 0.5rem;
          color: #1e293b;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .route-columns-container {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .view-route-container {
            padding: 1rem;
          }

          .view-header {
            padding: 1rem;
          }

          .route-title-section h1 {
            font-size: 1.25rem;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .tabs-header {
            flex-direction: column;
          }

          .tab-btn {
            border-bottom: none;
            border-left: 3px solid transparent;
          }

          .tab-btn.active {
            border-left-color: #00BCD4;
            border-bottom-color: transparent;
          }

          .stop-row {
            flex-wrap: wrap;
          }

          .time-badge {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default ViewRouteDetails;